import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await axios.get('/api/users/me');
          setUser(response.data);
        } catch (err) {
          console.error('Auth check failed:', err);
          localStorage.removeItem('accessToken');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    setError(null);
    try {
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      const response = await axios.post('/api/auth/token', formData);
      const { access_token, requires_gdpr_consent, user: userData } = response.data;
      
      // Check if GDPR consent is required
      if (requires_gdpr_consent) {
        // Return partial user data and a flag indicating GDPR consent is needed
        return { 
          requiresGdprConsent: true, 
          userData 
        };
      }
      
      // GDPR consent already given, proceed with login
      localStorage.setItem('accessToken', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser(userData);
      return { userData };
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
      throw err;
    }
  };

  // Register function
  const register = async (userData) => {
    setError(null);
    try {
      const response = await axios.post('/api/auth/register', userData);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
      throw err;
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('accessToken');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

    // Function to handle GDPR consent
  const submitGdprConsent = async (accept, userData) => {
    setError(null);
    try {
      if (!accept) {
        return false;
      }

      const response = await axios.post('/api/auth/gdpr-consent', { 
        accept, 
        user_id: userData.id,
        email: userData.email 
      });
      const { access_token } = response.data;
      
      localStorage.setItem('accessToken', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Get user data after GDPR consent
      const userResponse = await axios.get('/api/users/me');
      setUser(userResponse.data);
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit GDPR consent');
      return false;
    }
  };

  // Update user function
  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    submitGdprConsent,
    updateUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
